import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, Move } from "lucide-react";
import { api, formatApiError } from "../lib/api";

function parseFocal(str) {
  const m = (str || "50% 50%").match(/(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%/);
  if (!m) return { x: 50, y: 50 };
  return { x: +m[1], y: +m[2] };
}

export default function ImageUploader({
  value,
  onChange,
  testId = "image-uploader",
  aspect = "aspect-video",
  label = "Upload image",
  focal, // optional: "50% 50%" string to enable drag-to-position
  onFocalChange, // optional: (str) => void
}) {
  const fileRef = useRef(null);
  const imgWrapRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [positioning, setPositioning] = useState(false);
  const [dragging, setDragging] = useState(false);
  const focalEnabled = typeof onFocalChange === "function";
  const f = parseFocal(focal);

  async function handleFile(file) {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Only JPG, PNG, WebP, or GIF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function updateFocalFromEvent(e) {
    const rect = imgWrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const point = e.touches?.[0] || e;
    const x = ((point.clientX - rect.left) / rect.width) * 100;
    const y = ((point.clientY - rect.top) / rect.height) * 100;
    const clamped = (n) => Math.max(0, Math.min(100, n));
    onFocalChange(`${Math.round(clamped(x))}% ${Math.round(clamped(y))}%`);
  }

  useEffect(() => {
    if (!positioning) return;
    const up = () => setDragging(false);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [positioning]);

  return (
    <div data-testid={testId}>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {value ? (
        <>
          <div
            ref={imgWrapRef}
            className={`relative ${aspect} rounded-xl overflow-hidden border ${
              positioning ? "border-axe-tangerine ring-2 ring-axe-tangerine/40" : "border-axe-border"
            } bg-axe-bg ${positioning ? "cursor-crosshair select-none" : ""}`}
            onMouseDown={(e) => {
              if (!positioning) return;
              setDragging(true);
              updateFocalFromEvent(e);
            }}
            onMouseMove={(e) => {
              if (!positioning || !dragging) return;
              updateFocalFromEvent(e);
            }}
            onTouchStart={(e) => {
              if (!positioning) return;
              setDragging(true);
              updateFocalFromEvent(e);
            }}
            onTouchMove={(e) => {
              if (!positioning || !dragging) return;
              updateFocalFromEvent(e);
            }}
          >
            <img
              src={value}
              alt=""
              draggable={false}
              className="h-full w-full object-cover"
              style={{ objectPosition: focalEnabled ? `${f.x}% ${f.y}%` : undefined }}
            />
            {positioning && (
              <>
                {/* rule-of-thirds grid for alignment */}
                <div className="pointer-events-none absolute inset-0">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/25" />
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/25" />
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-white/25" />
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-white/25" />
                </div>
                {/* focal marker */}
                <div
                  className="pointer-events-none absolute h-6 w-6 rounded-full border-2 border-white bg-axe-tangerine/70 shadow-[0_0_0_2px_rgba(0,0,0,0.6),0_0_12px_rgba(255,140,0,0.7)]"
                  style={{
                    left: `calc(${f.x}% - 12px)`,
                    top: `calc(${f.y}% - 12px)`,
                  }}
                  data-testid={`${testId}-focal-marker`}
                />
                <div className="absolute left-2 top-2 rounded-full bg-black/70 text-[10px] uppercase tracking-[0.22em] font-bold text-white px-3 py-1">
                  Drag to set focus · {f.x}% {f.y}%
                </div>
              </>
            )}
            {!positioning && (
              <div className="absolute inset-0 bg-black/0 hover:bg-black/50 transition-colors flex items-center justify-center gap-2 opacity-0 hover:opacity-100">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-full bg-axe-purple px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-bold text-white"
                  data-testid={`${testId}-replace`}
                >
                  Replace
                </button>
                {focalEnabled && (
                  <button
                    type="button"
                    onClick={() => setPositioning(true)}
                    className="rounded-full bg-axe-tangerine px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-bold text-black inline-flex items-center gap-1"
                    data-testid={`${testId}-reposition`}
                  >
                    <Move size={12} /> Reposition
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onChange("")}
                  className="rounded-full bg-red-500/80 px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-bold text-white"
                  data-testid={`${testId}-clear`}
                >
                  <X size={12} className="inline" /> Remove
                </button>
              </div>
            )}
          </div>
          {positioning && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onFocalChange("50% 50%")}
                className="rounded-full border border-axe-border px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-neutral-300 hover:text-white"
                data-testid={`${testId}-focal-reset`}
              >
                Center
              </button>
              <button
                type="button"
                onClick={() => onFocalChange("50% 25%")}
                className="rounded-full border border-axe-border px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-neutral-300 hover:text-white"
              >
                Face / Top
              </button>
              <button
                type="button"
                onClick={() => setPositioning(false)}
                className="ml-auto rounded-full bg-axe-purple px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-white"
                data-testid={`${testId}-focal-done`}
              >
                Done
              </button>
            </div>
          )}
        </>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className={`w-full ${aspect} rounded-xl border-2 border-dashed border-axe-border bg-axe-bg hover:border-axe-purple/60 transition-colors flex flex-col items-center justify-center gap-2 text-neutral-400 disabled:opacity-50`}
          data-testid={`${testId}-trigger`}
        >
          {uploading ? (
            <Loader2 className="animate-spin text-axe-purple" size={26} />
          ) : (
            <Upload size={22} />
          )}
          <span className="text-xs uppercase tracking-[0.25em] font-bold">
            {uploading ? "Uploading…" : label}
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            JPG / PNG / WebP · Max 5MB
          </span>
        </button>
      )}
    </div>
  );
}
